import { first } from 'lodash';
import {
  MAP_OUTCOME_TO_SHORT_LABEL,
  OutcomeCode,
} from '../enums/hitters.enums';
import { MAX_HIT_DISTANCE_FT } from '../enums/pitches.enums';
import { IFireEvent } from '../interfaces/i-session-event';
import { IStrikeZone } from '../interfaces/i-strike-zone';
import { ISessionHit } from '../interfaces/session-hits/i-session-hit';
import { IMSBSShot, IVisionShot } from '../interfaces/training/i-msbs-shot';
import { IShotPrecursor } from '../interfaces/training/i-shot-precursor';
import { PlateHelper, VISIBLE_STRIKEZONE } from './plate.helper';
import { TrajHelper } from './trajectory.helper';

export class VisionHelper {
  // will trigger a warning if delta exceeds this value but is less than ALERT_DISTANCE_DELTA_FT
  static WARNING_DISTANCE_DELTA_FT = 1;

  // will trigger an alert if delta exceeds this value
  static ALERT_DISTANCE_DELTA_FT = 2;

  // todo: determine if we will ever enable this, deprecate if not
  static ALLOW_VISION_RELEASE_DISTANCE_UPDATE = false;

  // todo: determine if we will ever enable this, deprecate if not
  static ALLOW_OUT_OF_SPEC_VISION_DISTANCE_UPDATE = true;

  static getPrecursor(fire: IFireEvent, shot: IMSBSShot): IShotPrecursor {
    if (!shot.Traj) {
      throw new Error('No shot trajectory provided in StereoVisionHelper');
    }

    const confidence = shot.analysis_data?.confidence_metrics?.confidence;

    const output: IShotPrecursor = {
      source: 'vision',
      //! Trajectory release position is not adjusted for Vision traj's currently
      traj: shot.Traj,
      date: new Date(),
      confidence: {
        PITCH_SpeedConfidence: confidence?.speed ?? 0,
        PITCH_SpinConfidence: 0,
        PITCH_StrikePositionHeightConfidence: confidence?.plate_z ?? 0,
        PITCH_StrikePositionSideConfidence: confidence?.plate_x ?? 0,
      },

      bs: undefined,
      break: TrajHelper.getVisionBreaks(shot),
      seams: undefined,
      hit: fire.data.training
        ? undefined
        : VisionHelper.getHitPrecursor(fire, shot),
    };
    return output;
  }

  static getHitPrecursor(
    fire: IFireEvent,
    shot: IMSBSShot
  ): Partial<ISessionHit> | undefined {
    if (!fire.data.pitch_id) {
      throw new Error('Fire event has no pitch_id');
    }

    const traj = shot.Traj;

    const plate = traj ? TrajHelper.getPlateLoc(traj) : undefined;
    const breaks = traj ? TrajHelper.getBreaks(traj) : undefined;
    const speedMPH = traj ? TrajHelper.getSpeedFromTraj(traj) : undefined;

    const hit_info = shot.hit_data?.hit_info;
    const hitFromFireTs = shot.hit_data?.hit_and_swing_time_from_fire_ts;
    const swing_results = shot.hit_data?.swing_results;

    const output: Partial<ISessionHit> = {
      fire: {
        _id: `${fire._id}`, // ghetto version of stringifyID since it's not available in lib_ts
        machineID: fire.machine,
        session: fire.session,
        machine_timezone: fire.machine_timezone,
        // probably not defined @ spawn
        replay_video_id: fire.replay_video_id,
        annotated_video_id: fire.annotated_video_id,
      },

      pitch: {
        _id: fire.data.pitch_id,
        type: fire.data.pitch_type,
        name: fire.data.pitch_name,

        speedMPH: speedMPH ?? 0,
        xBreakIN: breaks?.xInches,
        zBreakIN: breaks?.zInches,

        plate: plate,

        release: traj
          ? {
              px: traj.px,
              py: traj.py,
              pz: traj.pz,
            }
          : undefined,
      },

      hit: {
        invalid: (hit_info?.distance_ft ?? 0) > MAX_HIT_DISTANCE_FT,
        swing:
          swing_results?.source === 'legacy'
            ? // always ignore legacy swing results
              null
            : swing_results?.label
              ? // something was reported
                swing_results.label === 'swing'
              : // nothing was reported, because it's disabled/not available
                null,

        exitMPH: hit_info?.exit_velocity_mph,
        vLaunchDEG: hit_info?.v_launch_angle_deg,
        hLaunchDEG: hit_info?.h_launch_angle_deg,

        // below formerly filled by python server, now provided directly by FW/Vision
        distanceFT: hit_info?.distance_ft ?? hit_info?.model_result?.distanceFT,
        outcome: hit_info?.model_result?.outcome ?? OutcomeCode.Unknown,
        hLandingDEG: hit_info?.model_result?.hLandingDEG,
        landingTime: hit_info?.model_result?.landingTime,
        pathPoints: hit_info?.model_result?.pathPoints,
        timeStep: hit_info?.model_result?.timeStep,
        exitOmegaRPM: hit_info?.model_result?.exitOmegaRPM,
        // if we get hit_and_swing_time_from_fire_ts, FW is done processing the hit
        processed: !!hitFromFireTs,
      },
      avg_excluded: false,
    };

    return output;
  }

  static getShot = (
    fire: IFireEvent,
    szDef?: IStrikeZone
  ): Partial<IVisionShot> => {
    const { msbs: msbsArr } = fire;

    const msbs = first(msbsArr);
    const precursor = msbs
      ? VisionHelper.getHitPrecursor(fire, msbs)
      : undefined;

    const precursorHit = precursor?.hit;
    const pitch = precursor?.pitch;

    // don't return data from invalid hits
    const hit = precursorHit?.invalid ? undefined : precursorHit;

    const output: Partial<IVisionShot> = {
      pitch_break_x_in: pitch?.xBreakIN ? -1 * pitch.xBreakIN : undefined,
      pitch_break_z_in: pitch?.zBreakIN,

      pitch_speed_mph: pitch?.speedMPH,

      pitch_plate_x_ft: pitch?.plate?.plate_x,
      pitch_plate_z_ft: pitch?.plate?.plate_z,

      pitch_pos_x_ft: pitch?.release?.px,
      pitch_pos_y_ft: pitch?.release?.py,
      pitch_pos_z_ft: pitch?.release?.pz,

      // todo: fetch hitter details so isStrike is based on hitter strike zone?
      pitch_strike: pitch?.plate
        ? PlateHelper.isStrike({
            plateFt: pitch.plate,
            szDef: szDef ?? VISIBLE_STRIKEZONE,
          })
        : undefined,

      hit_swing: hit?.swing ?? undefined, // omit the field entirely if the value is null
      hit_speed_mph: hit?.exitMPH,
      hit_horizontal_deg: hit?.hLaunchDEG,
      hit_vertical_deg: hit?.vLaunchDEG,
      hit_distance_ft: hit?.distanceFT,
      hit_outcome: hit?.outcome
        ? MAP_OUTCOME_TO_SHORT_LABEL[hit?.outcome]
        : undefined,
    };

    return output;
  };
}
